"use client";

import { Event } from "@/types/event.types";
import { Button } from "@heroui/react";
import Image from "next/image";
import Link from "next/link";
import { isPastEvent } from "@/lib/utils";
import { formatEventDate } from "@/lib/event-dates";

interface EventsListProps {
  events: Event[];
}

function EventCard({
  event,
  priority = false,
}: {
  event: Event;
  priority?: boolean;
}) {
  const startDate = new Date(event.start_date);
  const isPast = isPastEvent(startDate);

  const day = formatEventDate(event, event.start_date, "en-US", { day: "numeric" });
  const month = formatEventDate(event, event.start_date, "en-US", { month: "short" }).toUpperCase();
  const formattedDate = formatEventDate(event, event.start_date, "en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const eventUrl = `/events/${event.slug}`;

  return (
    <article className="group">
      <Link href={eventUrl} className="block">
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden rounded-sm">
          <Image
            src={event.cover_image_url || event.image_url}
            alt={`${event.name} event poster`}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            priority={priority}
            loading={priority ? "eager" : "lazy"}
            quality={80}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
          />
          <div className="absolute inset-0 bg-gradient-to-t from-media/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Date badge */}
          <div className="theme-media absolute top-3 left-3 bg-canvas/70 backdrop-blur-sm px-2.5 py-1.5 rounded-sm text-center min-w-[48px]">
            <span className="block text-lg font-black text-ink leading-none">
              {day}
            </span>
            <span className="block text-[10px] font-bold text-accent tracking-wider leading-tight mt-0.5">
              {month}
            </span>
          </div>

          {isPast && (
            <div className="absolute top-3 right-3 bg-surface-raised/80 backdrop-blur-sm text-muted text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
              Past
            </div>
          )}
        </div>

        {/* Content */}
        <div className="mt-3">
          <h3 className="text-base font-bold text-ink leading-snug line-clamp-1 group-hover:text-accent transition-colors duration-200">
            {event.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1.5 text-subtle text-xs">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <time dateTime={startDate.toISOString()}>
              {formattedDate}
            </time>
            <span className="text-subtle mx-0.5">|</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <span className="truncate">
              {event.cityName}, {event.countryName}
            </span>
          </div>
        </div>
      </Link>

      {/* Action buttons */}
      <div className="flex gap-2 mt-3">
        {event.website_url && (
          <Link
            href={eventUrl}
            className="inline-flex items-center justify-center bg-theme-neon text-on-accent text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-theme-neon/90 transition-colors"
          >
            Get Tickets
          </Link>
        )}
        <Link
          href={eventUrl}
          className="inline-flex items-center justify-center bg-surface-raised text-ink-secondary text-xs font-medium px-4 py-2 rounded-sm hover:bg-surface-hover hover:text-ink transition-colors"
        >
          Details
        </Link>
      </div>
    </article>
  );
}

export default function EventsList({ events }: EventsListProps) {
  if (events.length === 0) {
    return (
      <section className="w-full flex flex-col items-center py-20">
        <div className="w-16 h-16 rounded-full bg-surface flex items-center justify-center mb-4">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-subtle">
            <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2 className="text-lg font-bold text-ink mb-2">No events found</h2>
        <p className="text-sm text-subtle mb-6 text-center max-w-sm">
          Try adjusting your filters or search terms to discover more events.
        </p>
        <Button
          as={Link}
          href="/events"
          className="bg-theme-neon text-on-accent font-bold text-sm uppercase tracking-wider rounded-sm"
          aria-label="Clear all filters and view all events"
        >
          Clear Filters
        </Button>
      </section>
    );
  }

  return (
    <section
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10"
      aria-label="Events list"
    >
      {events.map((event: Event, index: number) => (
        <EventCard
          key={event.id || event.slug || `event-${index}`}
          event={event}
          priority={index < 6}
        />
      ))}
    </section>
  );
}
